import { useState } from 'react';
import { useProposals, type Proposal, type ProposalStatus, getValidityStatus, type ValidityFilter, VALIDITY_OPTIONS } from '@/hooks/useProposals';
import { useTemplatePresets } from '@/hooks/useTemplatePresets';
import { getCategories } from '@/mocks/template-presets';
import { useNavigate, Link } from 'react-router-dom';

export default function AdminProposalsPage() {
  const { proposals, duplicate, remove, renew, loading } = useProposals();
  const { presets } = useTemplatePresets();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | ProposalStatus>('all');
  const [validityFilter, setValidityFilter] = useState<ValidityFilter>('all');
  const [search, setSearch] = useState('');
  const [renewModal, setRenewModal] = useState<Proposal | null>(null);
  const [renewDays, setRenewDays] = useState<number | null>(30);
  const [renewLoading, setRenewLoading] = useState(false);

  const filtered = proposals.filter((p) => {
    const matchesFilter = filter === 'all' || p.status === filter;
    const matchesSearch =
      search === '' ||
      p.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.client_company?.toLowerCase().includes(search.toLowerCase()) ||
      p.proposal_number?.toLowerCase().includes(search.toLowerCase());

    let matchesValidity = true;
    if (validityFilter !== 'all') {
      const vs = getValidityStatus(p);
      if (validityFilter === 'valid') matchesValidity = !vs.isExpired && !vs.isExpiringSoon;
      if (validityFilter === 'expiring-soon') matchesValidity = vs.isExpiringSoon;
      if (validityFilter === 'expired') matchesValidity = vs.isExpired;
      if (validityFilter === 'no-expiration') matchesValidity = !p.expires_at;
    }

    return matchesFilter && matchesSearch && matchesValidity;
  });

  const statusLabel: Record<ProposalStatus, string> = {
    draft: 'Rascunho',
    sent: 'Enviada',
    viewed: 'Visualizada',
    approved: 'Já aprovada pessoalmente',
    rejected: 'Recusada',
    revision: 'Alteração Solicitada',
  };

  const statusColor: Record<ProposalStatus, string> = {
    draft: 'bg-gray-500/20 text-gray-400',
    sent: 'bg-blue-500/20 text-blue-400',
    viewed: 'bg-yellow-500/20 text-yellow-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    revision: 'bg-orange-500/20 text-orange-400',
  };

  const getCategoryName = (slug: string) => {
    const cat = getCategories().find((c) => c.slug === slug);
    return cat?.name || slug;
  };

  const getPresetName = (id: string | null) => {
    if (!id) return '-';
    const preset = presets.find((p) => p.id === id);
    return preset?.name || id;
  };

  const handleDuplicate = async (id: string) => {
    if (window.confirm('Deseja duplicar esta proposta?')) {
      await duplicate(id);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Deseja excluir esta proposta? Esta ação não pode ser desfeita.')) {
      await remove(id);
    }
  };

  const handleRenew = async () => {
    if (!renewModal) return;
    setRenewLoading(true);
    await renew(renewModal.id, renewDays);
    setRenewLoading(false);
    setRenewModal(null);
  };

  const ValidityBadge = ({ proposal }: { proposal: Proposal }) => {
    const status = getValidityStatus(proposal);
    const colorMap = {
      green: 'bg-green-500/15 text-green-400 border-green-500/20',
      yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
      red: 'bg-red-500/15 text-red-400 border-red-500/20',
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${colorMap[status.color]}`}>
        <i className={status.icon} />
        {status.label}
        {status.daysLeft !== null && (
          <span className="text-brand-text-muted">
            {status.daysLeft < 0 ? `(${Math.abs(status.daysLeft)} dias)` : `(${status.daysLeft} dias)`}
          </span>
        )}
      </span>
    );
  };

  const validityLabelMap: Record<ValidityFilter, string> = {
    all: 'Todas',
    valid: 'Válidas',
    'expiring-soon': 'Expiram em breve',
    expired: 'Vencidas',
    'no-expiration': 'Sem expiração',
  };

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Header */}
      <header className="h-14 bg-brand-darker border-b border-brand-gold/10 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-file-list-3-line text-brand-gold text-xl" />
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm">Painel Administrativo</h1>
            <p className="text-brand-text-muted text-xs">Propostas</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/library"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-folder-image-line" />
            <span className="hidden sm:inline">Biblioteca</span>
          </Link>
          <Link
            to="/admin/template"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-settings-line" />
            <span className="hidden sm:inline">Template</span>
          </Link>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-dashboard-line" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <button
            onClick={() => navigate('/admin/proposals/new')}
            className="flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium text-sm px-4 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Nova Proposta
          </button>
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
          >
            <i className="ri-home-line" />
            <span className="hidden sm:inline">Portal</span>
          </Link>
        </div>
      </header>

      {/* Filters */}
      <div className="px-5 py-4 border-b border-brand-gold/10 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'draft', 'sent', 'viewed', 'approved', 'rejected', 'revision'] as const).map(
            (s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  filter === s
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted hover:text-white border border-brand-gold/10'
                }`}
              >
                {s === 'all' ? 'Todas' : statusLabel[s]}
              </button>
            )
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'valid', 'expiring-soon', 'expired', 'no-expiration'] as ValidityFilter[]).map(
            (v) => (
              <button
                key={v}
                onClick={() => setValidityFilter(v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  validityFilter === v
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted hover:text-white border border-brand-gold/10'
                }`}
              >
                {validityLabelMap[v]}
              </button>
            )
          )}
        </div>
        <div className="relative w-full sm:w-64">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-brand-text-muted text-sm" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar proposta..."
            className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 pl-9 pr-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
          />
        </div>
      </div>

      {/* Table */}
      <div className="px-5 py-4">
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-brand-gold/10">
                  <th className="text-left text-brand-gold text-xs font-semibold p-3">Nº</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3">Cliente</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3 hidden md:table-cell">Empresa</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3 hidden lg:table-cell">Categoria</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3 hidden lg:table-cell">Preset</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3 hidden sm:table-cell">Status</th>
                  <th className="text-left text-brand-gold text-xs font-semibold p-3 hidden sm:table-cell">Validade</th>
                  <th className="text-right text-brand-gold text-xs font-semibold p-3">Total</th>
                  <th className="text-right text-brand-gold text-xs font-semibold p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="text-center text-brand-text-muted text-sm py-12">
                      <div className="w-8 h-8 border-4 border-gray-700 border-t-amber-500 rounded-full animate-spin mx-auto mb-3" />
                      Carregando propostas...
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-brand-text-muted text-sm py-12">
                      Nenhuma proposta encontrada.
                    </td>
                  </tr>
                )}
                {!loading && filtered.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-brand-gold/5 hover:bg-brand-gold/5 transition-colors"
                  >
                    <td className="text-brand-text text-sm p-3 font-mono">{p.proposal_number}</td>
                    <td className="text-brand-text text-sm p-3">{p.client_name}</td>
                    <td className="text-brand-text-muted text-sm p-3 hidden md:table-cell">
                      {p.client_company}
                    </td>
                    <td className="text-brand-text-muted text-sm p-3 hidden lg:table-cell">
                      {getCategoryName(p.category)}
                    </td>
                    <td className="text-brand-text-muted text-sm p-3 hidden lg:table-cell">
                      {getPresetName(p.template_preset_id)}
                    </td>
                    <td className="p-3 hidden sm:table-cell">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColor[p.status]}`}
                      >
                        {statusLabel[p.status]}
                      </span>
                    </td>
                    <td className="p-3 hidden sm:table-cell">
                      <ValidityBadge proposal={p} />
                    </td>
                    <td className="text-brand-gold text-sm p-3 text-right font-medium">
                      R${' '}
                      {p.total_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/proposta/${p.token}`)}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-brand-gold transition-colors cursor-pointer"
                          title="Visualizar"
                        >
                          <i className="ri-eye-line text-sm" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/proposals/edit/${p.id}`)}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-brand-gold transition-colors cursor-pointer"
                          title="Editar"
                        >
                          <i className="ri-pencil-line text-sm" />
                        </button>
                        <button
                          onClick={() => { setRenewModal(p); setRenewDays(p.validity_days ?? 30); }}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-brand-gold transition-colors cursor-pointer"
                          title="Renovar"
                        >
                          <i className="ri-refresh-line text-sm" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(p.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-brand-gold transition-colors cursor-pointer"
                          title="Duplicar"
                        >
                          <i className="ri-file-copy-line text-sm" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-500/10 text-brand-text-muted hover:text-red-400 transition-colors cursor-pointer"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-brand-gold/10 text-brand-text-muted text-xs">
            {filtered.length} proposta{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Renew Modal */}
      {renewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setRenewModal(null)} />
          <div className="relative bg-brand-card border border-brand-gold/30 rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-brand-gold text-lg font-semibold mb-1">Renovar Proposta</h3>
            <p className="text-brand-text-muted text-xs mb-4">
              {renewModal.proposal_number} — {renewModal.client_name}
            </p>
            <div className="mb-4">
              <label className="text-brand-text-muted text-xs mb-1 block">Nova validade</label>
              <select
                value={renewDays ?? ''}
                onChange={(e) => setRenewDays(e.target.value === '' ? null : Number(e.target.value))}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
              >
                {VALIDITY_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-brand-text-muted text-xs mt-2">
                A proposta será renovada a partir da data atual.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRenewModal(null)}
                className="flex-1 border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-2 rounded-md text-sm transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleRenew}
                disabled={renewLoading}
                className="flex-1 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-semibold py-2 rounded-md text-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                {renewLoading ? 'Renovando...' : 'Renovar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}